import { UtensilsCrossed, Shirt, Bath, Sparkles, Home } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const services = [
  {
    icon: Sparkles,
    title: 'Quick Light Clean',
    description: 'Affordable 1-hour general tidy-up. Light dusting, wiping surfaces and quick vacuum for a fresh home.',
    image: 'https://images.unsplash.com/photo-1758273238795-c284e5ae09b6?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    features: ['Light dusting', 'Surface wiping', 'Quick vacuum', 'General tidy-up'],
    pricing: 'From $50',
    affordable: true
  },
  {
    icon: Bath,
    title: 'Bathroom Deep Clean',
    description: 'Showers, tiles, grout, toilets, basins and mirrors cleaned and sanitised to a high standard.',
    image: 'https://images.unsplash.com/photo-1628602813647-c70518049674?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    features: ['Shower & tub scrubbing', 'Tile & grout cleaning', 'Toilet deep clean', 'Mirror polishing'],
    pricing: 'From $75'
  },
  {
    icon: UtensilsCrossed,
    title: 'Kitchen Deep Clean',
    description: 'Benches, sinks, stovetops, splashbacks and high-use surfaces degreased and refreshed.',
    image: 'https://customer-assets.emergentagent.com/job_wet-clean-pro/artifacts/3ojmzdlb_1000032885.jpg',
    features: ['Bench & sink cleaning', 'Stovetop degreasing', 'Splashback refresh', 'Surface sanitization'],
    pricing: 'From $95'
  },
  {
    icon: Shirt,
    title: 'Laundry Clean',
    description: 'Tubs, taps, surfaces and floors cleaned to remove dirt, residue and build-up.',
    image: 'https://images.unsplash.com/photo-1639010357069-64a5c260589f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    features: ['Tub & tap cleaning', 'Surface sanitization', 'Floor cleaning', 'Dirt & residue removal'],
    pricing: 'From $55'
  },
  {
    icon: Home,
    title: 'Airbnb & Short Stay Cleaning',
    description: 'We get your place guest-ready so you can enjoy better reviews and less stress between bookings.',
    image: 'https://images.unsplash.com/photo-1697462247864-338e7eba8c4c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    features: ['Guest-ready cleaning', 'Space reset & preparation', 'Linen change available', 'Same day turnaround'],
    pricing: 'From $100',
    premium: true
  },
  {
    icon: Sparkles,
    title: 'Vacate Wet Area Clean',
    description: 'Ideal for rental inspections, ensuring wet areas are clean and presentable.',
    image: 'https://customer-assets.emergentagent.com/job_wet-clean-pro/artifacts/cbajh338_1000032872.jpg',
    features: ['Kitchen, bathroom & laundry', 'Toilets, sinks & tiles', 'Inspection-ready standard', 'Wet areas only'],
    pricing: 'From $200',
    premium: true
  }
];

export const Services = () => {
  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We focus on the areas that matter most in your home
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card 
                key={index} 
                className="overflow-hidden border-2 border-gray-200 hover:border-teal-500 transition-all duration-300 hover:shadow-xl group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="bg-teal-600 p-3 rounded-lg">
                      <Icon className="text-white" size={24} />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <p className="text-gray-700 mb-4 text-lg leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-600">
                        <div className="w-1.5 h-1.5 bg-teal-600 rounded-full"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {service.pricing && (
                    <div className="pt-4 border-t border-gray-200">
                      <span className="text-2xl font-bold text-teal-600">{service.pricing}</span>
                      <span className="text-gray-500 ml-2">*</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <p className="text-sm text-gray-500 mb-4">
            * Final price may vary depending on size and condition. All services available by appointment only.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('booking');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-lg"
          >
            Get a Quote
          </button>
        </div>
      </div>
    </section>
  );
};
